import { CssAttributeName, CssNodeName } from '@common/flare-data/plugins/css-schema.plugin';
import { CssSchema } from '@common/flare-data/schemas/css-schema';
import { CssUtils } from '@common/flare-data/util/css-utils';
import { FlareSerializerExportOptions } from '@common/flare/types/to-flare-xml-options.type';
import { DocSizeError } from '@common/html/constants/doc-size-error.constant';
import { stripBOM } from '@common/html/util/xml';
import { NodeIdAttrName } from '@common/prosemirror/model/node-id';
import { Serializer } from '@common/prosemirror/model/serializer';
import { CustomError } from '@common/util/custom-error';
import { byteSize } from '@common/util/utf';
import { AnyNode, AtRule, Comment, Container, Declaration, Root, Rule, stringify } from 'postcss';
import { AtRuleProps, AtRuleRaws } from 'postcss/lib/at-rule';
import { CommentProps, CommentRaws } from 'postcss/lib/comment';
import { DeclarationProps, DeclarationRaws } from 'postcss/lib/declaration';
import { RootProps, RootRaws } from 'postcss/lib/root';
import { RuleProps, RuleRaws } from 'postcss/lib/rule';
import { Attrs, Fragment, ProseMirrorNode } from 'prosemirror-model';

/** Represents changes made in AST model. */
export interface AstChange {
  added: AnyNode[],
  removed: AnyNode[],
  moved: AnyNode[],
  changed: AnyNode[],
}

/** Provides methods for converting string, prosemirror document and AST models. */
export class CssSerializer implements Serializer {

  /** Converts the code string to a prosemirror document.
   * @param code The code string to convert.
   * @param schema The prosemirror document schema.
   * @param validate The function to validate the document.
   *   This function should throw error if the document is not valid.
   * @returns A prosemirror document.
  */
  codeToDoc(code: string, schema: CssSchema, validate?: (root: Root) => void): ProseMirrorNode {
    code = stripBOM(code);
    const error = this.validate(code, schema);
    if (error) {
      throw error;
    }
    const root = CssUtils.parse(code, true);
    validate?.(root);
    const doc = this.astToNode(root, schema);
    return schema.node('doc', null, doc);
  }

  /** Serializes the prosemirror node to string.
   * @param node The prosemirror node to serialize.
   * @param schema The prosemirror document schema.
   * @param options Serialization options.
   * @returns String representation of the prosemirror node.
  */
  nodeToCode(node: ProseMirrorNode, schema: CssSchema, options?: FlareSerializerExportOptions): string {
    let code = '';
    const ast: AnyNode = this.nodeToAst(node);
    stringify(ast, (part, node) => {
      if (node?.type === CssNodeName.comment && node.raws.inline === true) {
        code += '//' + node.raws.left + node.text + node.raws.right;
      } else {
        code += part;
      }
    });
    this.throwIfDocTooLarge(code, schema.docMaxCharLength);
    return code;
  }

  /** Serializes the prosemirror fragment to string.
   * @param fragment The prosemirror fragment to serialize.
   * @param schema The prosemirror document schema.
   * @param options Serialization options.
   * @returns String representation of the prosemirror fragment.
  */
  fragmentToCode(fragment: Fragment, schema: CssSchema, options?: FlareSerializerExportOptions): string {
    let code = '';
    fragment.forEach(node => code += this.nodeToCode(node, schema, options));
    return code;
  }

  /** Checks whether the code string can be parsed to the prosemirror document.
   * @param code The code string to validate.
   * @param schema The result prosemirror document schema.
   * @param options Validation options.
  */
  validate(code: string, schema: CssSchema): Error | null {
    let error = null;
    try {
      this.throwIfDocTooLarge(code, schema.docMaxCharLength);
      error = CssUtils.validate(code);
    }
    catch (err) {
      error = error || err;
    }
    return error;
  }

  /** Converts AST node to a prosemirror node. */
  astToNode(ast: AnyNode, schema: CssSchema): ProseMirrorNode {
    const content: ProseMirrorNode[] = [];
    switch (ast.type) {
      case CssNodeName.root: {
        const raws: RootRaws = ast.raws;
        const attrs: Attrs = {
          [NodeIdAttrName]: raws?.id,
          [CssAttributeName.type]: raws?.type,
          [CssAttributeName.after]: raws?.after,
          [CssAttributeName.codeAfter]: raws?.codeAfter,
          [CssAttributeName.codeBefore]: raws?.codeBefore,
          [CssAttributeName.semicolon]: this.booleanToString(raws?.semicolon),
        };
        ast.each(child => { content.push(this.astToNode(child, schema)); });
        return schema.node(ast.type, attrs, content);
      }
      case CssNodeName.atRule: {
        const raws: AtRuleRaws = ast.raws;
        const params: { raw: string, value: string } = raws?.params;
        const attrs: Attrs = {
          [NodeIdAttrName]: raws?.id,
          [CssAttributeName.name]: ast.name,
          [CssAttributeName.params]: ast.params,
          [CssAttributeName.after]: raws?.after,
          [CssAttributeName.afterName]: raws?.afterName,
          [CssAttributeName.before]: raws?.before,
          [CssAttributeName.between]: raws?.between,
          [CssAttributeName.ownSemicolon]: this.booleanToString(!ast.nodes), // detect whether at-rule uses ; or {}
          [CssAttributeName.semicolon]: this.booleanToString(raws?.semicolon),
          [CssAttributeName.paramsRaw]: params?.raw,
          [CssAttributeName.paramsRawValue]: params?.value,
        }
        ast.each(child => { content.push(this.astToNode(child, schema)); });
        return schema.node(ast.type, attrs, content);
      }
      case CssNodeName.rule: {
        const raws: RuleRaws = ast.raws;
        const selector: { raw: string, value: string } = raws?.selector;
        const attrs: Attrs = {
          [NodeIdAttrName]: raws?.id,
          [CssAttributeName.selector]: ast.selector,
          [CssAttributeName.after]: raws?.after,
          [CssAttributeName.before]: raws?.before,
          [CssAttributeName.between]: raws?.between,
          [CssAttributeName.ownSemicolon]: raws?.ownSemicolon,
          [CssAttributeName.semicolon]: this.booleanToString(raws?.semicolon),
          [CssAttributeName.selectorRaw]: selector?.raw,
          [CssAttributeName.selectorRawValue]: selector?.value,
        }
        ast.each(child => { content.push(this.astToNode(child, schema)); });
        return schema.node(ast.type, attrs, content);
      }
      case CssNodeName.declaration: {
        const raws: DeclarationRaws = ast.raws;
        const value: { raw: string, value: string } = raws?.value;
        const attrs: Attrs = {
          [NodeIdAttrName]: raws?.id,
          [CssAttributeName.prop]: ast.prop,
          [CssAttributeName.value]: ast.value,
          [CssAttributeName.important]: this.booleanToString(ast.important),
          [CssAttributeName.variable]: this.booleanToString(ast.variable),
          [CssAttributeName.before]: raws?.before,
          [CssAttributeName.between]: raws?.between,
          [CssAttributeName.importantRawValue]: raws?.important,
          [CssAttributeName.valueRaw]: value?.raw,
          [CssAttributeName.valueRawValue]: value?.value,
        }
        return schema.node(ast.type, attrs, content);
      }
      case CssNodeName.comment: {
        const raws: CommentRaws = ast.raws;
        const attrs: Attrs = {
          [NodeIdAttrName]: raws?.id,
          [CssAttributeName.text]: ast.text,
          [CssAttributeName.before]: raws?.before,
          [CssAttributeName.left]: raws?.left,
          [CssAttributeName.right]: raws?.right,
          [CssAttributeName.inline]: raws?.inline,
        }
        return schema.node(ast.type, attrs, content);
      }
      default: {
        throw new Error('Unknown CSS syntax node type.');
      }
    };
  }

  /** Converts the prosemirror node to AST node. */
  nodeToAst(node: ProseMirrorNode): AnyNode {
    const nodes: AnyNode[] = [];
    const raws = this.nodeToAstRaws(node);
    for (let i = 0; i < node.childCount; i++) {
      nodes.push(this.nodeToAst(node.child(i)));
    }
    switch (node.type.name) {
      case 'doc': {
        return nodes[0];
      }
      case CssNodeName.root: {
        const props: RootProps = {
          raws: raws,
          nodes: nodes,
        };
        return new Root(props);
      }
      case CssNodeName.atRule: {
        const props: AtRuleProps = {
          raws: raws,
          nodes: nodes,
          name: node.attrs[CssAttributeName.name],
          params: node.attrs[CssAttributeName.params],
        };
        if (this.stringToBoolean(node.attrs[CssAttributeName.ownSemicolon])) {
          delete props.nodes;  // remove the 'nodes' property to write ; after at-rule
        }
        return new AtRule(props);
      }
      case CssNodeName.rule: {
        const props: RuleProps = {
          raws: raws,
          nodes: nodes,
          selector: node.attrs[CssAttributeName.selector],
        };
        return new Rule(props);
      }
      case CssNodeName.declaration: {
        const props: DeclarationProps = {
          raws: raws,
          prop: node.attrs[CssAttributeName.prop],
          value: node.attrs[CssAttributeName.value],
          important: node.attrs[CssAttributeName.important],
        };
        return new Declaration(props);
      }
      case CssNodeName.comment: {
        const props: CommentProps = {
          raws: raws,
          text: node.attrs[CssAttributeName.text],
        };
        return new Comment(props);
      }
      default: {
        throw new Error('Unknown ProseMirror node type.');
      }
    }
  }

  /** Applies the prosemirror node state to the AST node state.
   * @param node The prosemirror node which state to apply.
   * @param ast The AST node to synchronize with prosemirror node.
   * @param change The changes accumulator.
   */
  applyNodeToAst(node: ProseMirrorNode, ast: AnyNode, change: AstChange): void {
    let changed = this.applyNodeToAstRaws(node, ast);
    const attrs = node.attrs;
    switch (node.type.name) {
      case CssNodeName.root: {
        const root = ast as Root;
        this.applyNodeChildren(node, root, change);
        break;
      }
      case CssNodeName.atRule: {
        const atRule = ast as AtRule;
        if (atRule.name !== attrs[CssAttributeName.name]) {
          atRule.name = attrs[CssAttributeName.name];
          changed = true;
        }
        if (atRule.params !== attrs[CssAttributeName.params]) {
          atRule.params = attrs[CssAttributeName.params];
          changed = true;
        }
        this.applyNodeChildren(node, atRule, change);
        break;
      }
      case CssNodeName.rule: {
        const rule = ast as Rule;
        if (rule.selector !== attrs[CssAttributeName.selector]) {
          rule.selector = attrs[CssAttributeName.selector];
          changed = true;
        }
        this.applyNodeChildren(node, rule, change);
        break;
      }
      case CssNodeName.declaration: {
        const decl = ast as Declaration;
        if (decl.prop !== attrs[CssAttributeName.prop]) {
          decl.prop = attrs[CssAttributeName.prop];
          changed = true;
        }
        if (decl.value !== attrs[CssAttributeName.value]) {
          decl.value = attrs[CssAttributeName.value];
          changed = true;
        }
        if (decl.important !== attrs[CssAttributeName.important]) {
          decl.important = attrs[CssAttributeName.important];
          changed = true;
        }
        break;
      }
      case CssNodeName.comment: {
        const comment = ast as Comment;
        if (comment.text !== attrs[CssAttributeName.text]) {
          comment.text = attrs[CssAttributeName.text];
          changed = true;
        }
        break;
      }
    }
    if (changed) {
      change.changed.push(ast);
    }
  }

  /** Converts the prosemirror node to AST node raws. */
  private nodeToAstRaws(node: ProseMirrorNode): Record<string, any> {
    switch (node.type.name) {
      case CssNodeName.root: {
        const raws: RootRaws = {
          id: node.attrs[NodeIdAttrName],
          type: node.attrs[CssAttributeName.type],
          after: node.attrs[CssAttributeName.after],
          codeAfter: node.attrs[CssAttributeName.codeAfter],
          codeBefore: node.attrs[CssAttributeName.codeBefore],
          semicolon: this.stringToBoolean(node.attrs[CssAttributeName.semicolon]),
        };
        return raws;
      }
      case CssNodeName.atRule: {
        const raws: AtRuleRaws = {
          id: node.attrs[NodeIdAttrName],
          after: node.attrs[CssAttributeName.after],
          afterName: node.attrs[CssAttributeName.afterName],
          before: node.attrs[CssAttributeName.before],
          between: node.attrs[CssAttributeName.between],
          ownSemicolon: node.attrs[CssAttributeName.ownSemicolon],
          semicolon: this.stringToBoolean(node.attrs[CssAttributeName.semicolon]),
          params: this.nodeToAstRawValue(node),
        };
        return raws;
      }
      case CssNodeName.rule: {
        const raws: RuleRaws = {
          id: node.attrs[NodeIdAttrName],
          after: node.attrs[CssAttributeName.after],
          before: node.attrs[CssAttributeName.before],
          between: node.attrs[CssAttributeName.between],
          ownSemicolon: node.attrs[CssAttributeName.ownSemicolon],
          semicolon: this.stringToBoolean(node.attrs[CssAttributeName.semicolon]),
          selector: this.nodeToAstRawValue(node),
        };
        return raws;
      }
      case CssNodeName.declaration: {
        const raws: DeclarationRaws = {
          id: node.attrs[NodeIdAttrName],
          before: node.attrs[CssAttributeName.before],
          between: node.attrs[CssAttributeName.between],
          important: node.attrs[CssAttributeName.importantRawValue],
          value: this.nodeToAstRawValue(node),
        };
        return raws;
      }
      case CssNodeName.comment: {
        const raws: CommentRaws = {
          id: node.attrs[NodeIdAttrName],
          before: node.attrs[CssAttributeName.before],
          left: node.attrs[CssAttributeName.left],
          right: node.attrs[CssAttributeName.right],
          inline: node.attrs[CssAttributeName.inline],
        };
        return raws;
      }
      default: {
        return undefined;
      }
    }
  }

  /** Converts the prosemirror node to AST raw value. */
  private nodeToAstRawValue(node: ProseMirrorNode): { raw: string, value: string } | undefined {
    const rv = { raw: undefined, value: undefined };
    switch (node.type.name) {
      case CssNodeName.atRule: {
        rv.raw = node.attrs[CssAttributeName.paramsRaw];
        rv.value = node.attrs[CssAttributeName.paramsRawValue];
        break;
      }
      case CssNodeName.rule: {
        rv.raw = node.attrs[CssAttributeName.selectorRaw];
        rv.value = node.attrs[CssAttributeName.selectorRawValue];
        break;
      }
      case CssNodeName.declaration: {
        rv.raw = node.attrs[CssAttributeName.valueRaw];
        rv.value = node.attrs[CssAttributeName.valueRawValue];
        break;
      }
    }
    return (rv.raw || rv.value) ? rv : undefined;
  }

  /** Applies the prosemirror node state to the AST node raws.
   * @param node The prosemirror node which state to apply.
   * @param ast The AST node which raws to synchronize with prosemirror node.
   * @returns True if the AST node raws were changed.
   */
  private applyNodeToAstRaws(node: ProseMirrorNode, ast: AnyNode): boolean {
    if (!ast.raws) {
      ast.raws = this.nodeToAstRaws(node);
      return true;
    }
    let changed = false;
    const attrs = node.attrs;
    switch (node.type.name) {
      case CssNodeName.root: {
        const raws: RootRaws = ast.raws;
        if (raws.id !== node.attrs[NodeIdAttrName]) {
          raws.id = node.attrs[NodeIdAttrName];
          changed = true;
        }
        if (raws.type !== attrs[CssAttributeName.type]) {
          raws.type = attrs[CssAttributeName.type];
          changed = true;
        }
        if (raws.after !== attrs[CssAttributeName.after]) {
          raws.after = attrs[CssAttributeName.after];
          changed = true;
        }
        if (raws.codeAfter !== attrs[CssAttributeName.codeAfter]) {
          raws.codeAfter = attrs[CssAttributeName.codeAfter];
          changed = true;
        }
        if (raws.codeBefore !== attrs[CssAttributeName.codeBefore]) {
          raws.codeBefore = attrs[CssAttributeName.codeBefore];
          changed = true;
        }
        if (raws.semicolon !== this.stringToBoolean(attrs[CssAttributeName.semicolon])) {
          raws.semicolon = this.stringToBoolean(attrs[CssAttributeName.semicolon]);
          changed = true;
        }
        break;
      }
      case CssNodeName.atRule: {
        const raws: AtRuleRaws = ast.raws;
        if (raws.id !== node.attrs[NodeIdAttrName]) {
          raws.id = node.attrs[NodeIdAttrName];
          changed = true;
        }
        if (raws.after !== attrs[CssAttributeName.after]) {
          raws.after = attrs[CssAttributeName.after];
          changed = true;
        }
        if (raws.afterName !== attrs[CssAttributeName.afterName]) {
          raws.afterName = attrs[CssAttributeName.afterName];
          changed = true;
        }
        if (raws.before !== attrs[CssAttributeName.before]) {
          raws.before = attrs[CssAttributeName.before];
          changed = true;
        }
        if (raws.between !== attrs[CssAttributeName.between]) {
          raws.between = attrs[CssAttributeName.between];
          changed = true;
        }
        if (raws.semicolon !== this.stringToBoolean(attrs[CssAttributeName.semicolon])) {
          raws.semicolon = this.stringToBoolean(attrs[CssAttributeName.semicolon]);
          changed = true;
        }
        if (this.applyNodeToAstRawValue(node, ast))
          changed = true;
        break;
      }
      case CssNodeName.rule: {
        const raws: RuleRaws = ast.raws;
        if (raws.id !== node.attrs[NodeIdAttrName]) {
          raws.id = node.attrs[NodeIdAttrName];
          changed = true;
        }
        if (raws.after !== attrs[CssAttributeName.after]) {
          raws.after = attrs[CssAttributeName.after];
          changed = true;
        }
        if (raws.before !== attrs[CssAttributeName.before]) {
          raws.before = attrs[CssAttributeName.before];
          changed = true;
        }
        if (raws.between !== attrs[CssAttributeName.between]) {
          raws.between = attrs[CssAttributeName.between];
          changed = true;
        }
        if (raws.ownSemicolon !== attrs[CssAttributeName.ownSemicolon]) {
          raws.ownSemicolon = attrs[CssAttributeName.ownSemicolon];
          changed = true;
        }
        if (raws.semicolon !== this.stringToBoolean(attrs[CssAttributeName.semicolon])) {
          raws.semicolon = this.stringToBoolean(attrs[CssAttributeName.semicolon]);
          changed = true;
        }
        if (this.applyNodeToAstRawValue(node, ast))
          changed = true;
        break;
      }
      case CssNodeName.declaration: {
        const raws: DeclarationRaws = ast.raws;
        if (raws.id !== node.attrs[NodeIdAttrName]) {
          raws.id = node.attrs[NodeIdAttrName];
          changed = true;
        }
        if (raws.before !== attrs[CssAttributeName.before]) {
          raws.before = attrs[CssAttributeName.before];
          changed = true;
        }
        if (raws.between !== attrs[CssAttributeName.between]) {
          raws.between = attrs[CssAttributeName.between];
          changed = true;
        }
        if (raws.important = attrs[CssAttributeName.important]) {
          raws.important = attrs[CssAttributeName.important];
          changed = true;
        }
        if (this.applyNodeToAstRawValue(node, ast))
          changed = true;
        break;
      }
      case CssNodeName.comment: {
        const raws: CommentRaws = ast.raws;
        if (raws.id !== node.attrs[NodeIdAttrName]) {
          raws.id = node.attrs[NodeIdAttrName];
          changed = true;
        }
        if (raws.before !== attrs[CssAttributeName.before]) {
          raws.before = attrs[CssAttributeName.before];
          changed = true;
        }
        if (raws.left !== attrs[CssAttributeName.left]) {
          raws.left = attrs[CssAttributeName.left];
          changed = true;
        }
        if (raws.right !== attrs[CssAttributeName.right]) {
          raws.right = attrs[CssAttributeName.right];
          changed = true;
        }
        if (raws.inline !== attrs[CssAttributeName.inline]) {
          raws.inline = attrs[CssAttributeName.inline];
          changed = true;
        }
        break;
      }
    }
    return changed;
  }

  /** Applies the prosemirror node state to the AST node raw value.
   * @param node The prosemirror node which state to apply.
   * @param ast The AST node which raw value to synchronize with prosemirror node.
   * @returns True if the AST node raw value were changed.
   */
  private applyNodeToAstRawValue(node: ProseMirrorNode, ast: AnyNode): boolean {
    let changed = false;
    const attrs = node.attrs;
    switch (node.type.name) {
      case CssNodeName.atRule: {
        const raws: AtRuleRaws = ast.raws;
        if (!raws.params) {
          const params = this.nodeToAstRawValue(node);
          if (params) {
            raws.params = params;
            changed = true;
          }
        } else {
          const params = raws.params;
          if (params.raw !== attrs[CssAttributeName.paramsRaw]) {
            params.raw = attrs[CssAttributeName.paramsRaw];
            changed = true;
          }
          if (params.value !== attrs[CssAttributeName.paramsRawValue]) {
            params.value = attrs[CssAttributeName.paramsRawValue];
            changed = true;
          }
        }
        break;
      }
      case CssNodeName.rule: {
        const raws: RuleRaws = ast.raws;
        if (!raws.selector) {
          const selector = this.nodeToAstRawValue(node);
          if (selector) {
            raws.selector = selector;
            changed = true;
          }
        } else {
          const selector = raws.selector;
          if (selector.raw !== attrs[CssAttributeName.selectorRaw]) {
            selector.raw = attrs[CssAttributeName.selectorRaw];
            changed = true;
          }
          if (selector.value !== attrs[CssAttributeName.selectorRawValue]) {
            selector.value = attrs[CssAttributeName.selectorRawValue];
            changed = true;
          }
        }
        break;
      }
      case CssNodeName.declaration: {
        const raws: DeclarationRaws = ast.raws;
        if (!raws.value) {
          const value = this.nodeToAstRawValue(node);
          if (value) {
            raws.value = value;
            changed = true;
          }
        } else {
          const value = raws.value;
          if (value.raw !== attrs[CssAttributeName.valueRaw]) {
            value.raw = attrs[CssAttributeName.valueRaw];
            changed = true;
          }
          if (value.value !== attrs[CssAttributeName.valueRawValue]) {
            value.value = attrs[CssAttributeName.valueRawValue];
            changed = true;
          }
        }
        break;
      }
    }
    return changed;
  }

  /** Synchronizes children of the prosemirror node with the AST node.
   * @param node The prosemirror node which children to synchronize.
   * @param ast The AST node which children to synchronize with prosemirror node.
   * @param change The changes accumulator.
   */
  private applyNodeChildren(node: ProseMirrorNode, ast: Container, change: AstChange): void {
    let i = 0;
    let map: Map<string, AnyNode> = null;
    // try synchronize common count of prosemirror and AST node children
    const count = Math.min(node.childCount, ast.nodes.length);
    for (; i < count; i++) {
      let childAst = ast.nodes[i];
      const childNode = node.child(i);
      // compare nodes id
      const nodeId = childNode.attrs[NodeIdAttrName];
      const astId = childAst.raws?.id;
      if (nodeId === astId) {
        this.applyNodeToAst(childNode, childAst, change);
      } else {
        // if nodes id are different, clear the rest of
        // AST children collection and move then to the map
        map = new Map<string, AnyNode>();
        for (; i < ast.nodes.length;) {
          ast.removeChild(childAst);
          map.set(childAst.raws?.id as string, childAst);
          childAst = ast.nodes[i];
        }
        break;
      }
    }
    // if we didn't reach the end of children collection then
    // fill the rest of AST children collection with new nodes
    // or nodes from  previously prepared map
    for (; i < node.childCount; i++) {
      let childAst: AnyNode = null;
      const childNode = node.child(i);
      const nodeId = childNode.attrs[NodeIdAttrName];
      if (map?.has(nodeId)) {
        // get the existing AST node to push at the new position
        childAst = map.get(nodeId);
        this.applyNodeToAst(childNode, childAst, change);
        change.moved.push(childAst);
        map.delete(nodeId);
      } else {
        // create a new AST node
        childAst = this.nodeToAst(childNode);
        change.added.push(childAst);
      }
      ast.append(childAst);
    }
    // clear the rest AST nodes from the collection
    // in case there were only last children deletion from the prosemirror node
    for (; i < ast.nodes.length;) {
      const childAst = ast.nodes[i];
      change.removed.push(childAst);
      ast.removeChild(childAst);
    }
    // the rest of AST child nodes which id not used
    // in prosemirror child node collection where removed
    map?.forEach(childAst => change.removed.push(childAst));
  }

  /** Converts AST node boolean property value to prosemirror node attribute string value. */
  private booleanToString(value: Boolean | undefined): string | undefined {
    if (typeof value !== 'boolean') {
      return undefined;
    }
    return value ? 'true' : 'false';
  }

  /** Converts prosemirror node attribute string value to AST node boolean property value. */
  private stringToBoolean(value: String | undefined): boolean | undefined {
    if (typeof value !== 'string') {
      return undefined;
    }
    switch (value) {
      case 'true':
        return true;
      case 'false':
        return false;
    }
    return undefined;
  }

  /** Checks whether the code string size bigger than allowed, if true raises an error. */
  private throwIfDocTooLarge(markup: string, maxDocCharLength: number): void {
    if (typeof maxDocCharLength === 'number' && markup?.length > maxDocCharLength) {
      const error = new CustomError(DocSizeError, `Cannot load document, document is ${markup.length} characters long and must be ${maxDocCharLength} characters or less in length.`);
      (error as any).docLength = markup.length;
      (error as any).docSize = byteSize(markup);
      throw error;
    }
  }
}
